import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  Lock,
  Mail,
  MapPin,
  Trash2,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const BIN_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8];

export default function QuoteWidget() {
  const navigate = useNavigate();
  const [zip, setZip] = useState("");
  const [bins, setBins] = useState(2);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submitQuote = async (e) => {
    e.preventDefault();
    if (!/^\d{5}$/.test(zip)) {
      toast.error("Enter a 5-digit ZIP code");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/quote", { zip, bin_count: bins });
      setResult(data);
    } catch (err) {
      toast.error("Couldn't fetch quote", { description: formatApiError(err) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="relative bg-white rounded-2xl p-6 sm:p-7 lg:p-8 max-w-md mx-auto md:max-w-md md:mx-auto lg:max-w-none lg:mx-0 shadow-[0_20px_45px_-15px_rgba(15,23,42,0.25)] ring-1 ring-slate-200/70 lg:ring-0 lg:shadow-lift"
      data-testid="quote-widget"
    >
      {/* Mobile/tablet — gold hairline accent */}
      <div
        aria-hidden="true"
        className="lg:hidden absolute top-3 left-6 sm:left-7 right-6 sm:right-7 h-[2.5px] bg-gradient-to-r from-transparent via-gold-400 to-transparent opacity-90 rounded-full"
      />

      {/* Mobile/tablet — headline + subhead */}
      <div className="lg:hidden mb-6 mt-3">
        <h3 className="font-heading font-extrabold uppercase tracking-tight text-ink leading-[1.05] mb-3 text-[clamp(1.5rem,6.5vw,2rem)]">
          Get Your Instant Quote
        </h3>
        <div className="space-y-1 mt-2 mb-1">
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-medium">
            Serving
          </p>
          <p className="text-sm sm:text-[15px] font-semibold text-slate-700 flex flex-wrap gap-x-2">
            <span>Pine Hill and nearby South Jersey communities</span>
          </p>
        </div>
      </div>

      {/* Desktop — original headline (unchanged) */}
      <h3 className="hidden lg:block font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight text-ink mb-5 lg:mb-6 leading-[1.05]">
        Get Your Instant Quote
      </h3>

      {/* ---------- MOBILE / TABLET FORM (<lg) ---------- */}
      <form
        onSubmit={submitQuote}
        className="lg:hidden space-y-4"
        data-testid="quote-form-mobile"
        noValidate
      >
        <div>
          <label
            htmlFor="quote-zip-m"
            className="block text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-2"
          >
            Zip Code
          </label>
          <div className="relative">
            <MapPin
              size={16}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              aria-hidden="true"
            />
            <input
              id="quote-zip-m"
              type="text"
              inputMode="numeric"
              maxLength={5}
              value={zip}
              onChange={(e) => setZip(e.target.value.replace(/\D/g, ""))}
              placeholder="08021"
              aria-label="Zip code"
              className="h-14 md:h-16 w-full pl-11 pr-4 text-base font-medium rounded-xl bg-slate-50 border border-slate-200 text-ink placeholder:text-slate-400 transition-all focus:bg-white focus:border-brand focus:ring-2 focus:ring-brand/25 focus:outline-none"
              data-testid="quote-input-zip-mobile"
            />
          </div>
        </div>

        <div>
          <label
            htmlFor="quote-bins-m"
            className="block text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-2"
          >
            Number of Bins
          </label>
          <div className="relative">
            <Trash2
              size={16}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              aria-hidden="true"
            />
            <ChevronDown
              size={16}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              aria-hidden="true"
            />
            <select
              id="quote-bins-m"
              value={bins}
              onChange={(e) => setBins(parseInt(e.target.value, 10))}
              aria-label="Number of bins"
              className="appearance-none h-14 md:h-16 w-full pl-11 pr-10 text-base font-medium rounded-xl bg-slate-50 border border-slate-200 text-ink transition-all focus:bg-white focus:border-brand focus:ring-2 focus:ring-brand/25 focus:outline-none"
              data-testid="quote-input-bins-mobile"
            >
              {BIN_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n} {n === 1 ? "bin" : "bins"}
                </option>
              ))}
            </select>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="quote-cta-shimmer relative overflow-hidden h-14 md:h-16 w-full inline-flex items-center justify-center gap-2 text-white text-base md:text-lg font-bold rounded-xl shadow-lg shadow-brand-500/30 transition-transform active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-4 focus:ring-brand/25"
          data-testid="quote-submit-mobile"
        >
          <span className="relative z-10 inline-flex items-center gap-2 pointer-events-none">
            {loading ? "Calculating…" : "Get My Price"}
            {!loading && <ArrowRight size={18} />}
          </span>
        </button>

        {/* Trust chips */}
        <div
          className="flex items-center justify-center gap-3 text-[11px] text-slate-500 pt-1"
          data-testid="quote-trust-chips"
        >
          <span className="inline-flex items-center gap-1">
            <Zap size={11} className="text-slate-400" aria-hidden="true" />
            Instant
          </span>
          <span aria-hidden="true" className="text-slate-300">
            ·
          </span>
          <span className="inline-flex items-center gap-1">
            <Lock size={11} className="text-slate-400" aria-hidden="true" />
            No card needed
          </span>
          <span aria-hidden="true" className="text-slate-300">
            ·
          </span>
          <span className="inline-flex items-center gap-1">
            <Mail size={11} className="text-slate-400" aria-hidden="true" />
            No spam
          </span>
        </div>
      </form>

      {/* ---------- DESKTOP FORM (lg+) — unchanged ---------- */}
      <form
        onSubmit={submitQuote}
        className="hidden lg:flex lg:items-stretch gap-3"
        data-testid="quote-form"
      >
        <input
          id="quote-zip"
          type="text"
          inputMode="numeric"
          maxLength={5}
          value={zip}
          onChange={(e) => setZip(e.target.value.replace(/\D/g, ""))}
          placeholder="Your zip code"
          aria-label="Zip code"
          className="h-14 lg:h-16 min-w-0 flex-1 px-5 text-lg font-medium rounded-xl border-2 border-slate-200 bg-white text-ink placeholder:text-slate-400 transition-all duration-200 focus:outline-none focus:border-brand focus:ring-4 focus:ring-brand/15"
          data-testid="quote-input-zip"
        />
        <select
          id="quote-bins"
          value={bins}
          onChange={(e) => setBins(parseInt(e.target.value, 10))}
          aria-label="Number of bins"
          className="h-14 lg:h-16 lg:w-[128px] shrink-0 px-4 text-lg font-medium rounded-xl border-2 border-slate-200 bg-white text-ink transition-all duration-200 focus:outline-none focus:border-brand focus:ring-4 focus:ring-brand/15"
          data-testid="quote-input-bins"
        >
          {BIN_OPTIONS.map((n) => (
            <option key={n} value={n}>
              {n} {n === 1 ? "bin" : "bins"}
            </option>
          ))}
        </select>
        <button
          type="submit"
          disabled={loading}
          className="h-14 lg:h-16 shrink-0 inline-flex items-center justify-center gap-2 whitespace-nowrap bg-brand text-white text-base lg:text-lg font-bold rounded-xl px-6 lg:px-6 transition-all duration-200 hover:bg-brand-600 hover:-translate-y-0.5 hover:shadow-lift focus:outline-none focus:ring-4 focus:ring-brand/25 disabled:opacity-60 disabled:cursor-not-allowed"
          data-testid="quote-submit"
        >
          {loading ? "Calculating…" : "Get My Price"}
          {!loading && <ArrowRight size={18} />}
        </button>
      </form>

      {result?.in_service && (
        <div
          className="mt-5 rounded-2xl bg-accent-50 border border-accent-200 p-6 sm:p-7 animate-fade-up text-center lg:text-left"
          data-testid="quote-result-in-service"
        >
          {/* IN RANGE chip */}
          <div className="inline-flex items-center gap-1.5 text-accent-700 mb-4">
            <MapPin size={12} />
            <span className="text-[11px] font-bold uppercase tracking-[0.2em]">
              In Range · {result.zip}
            </span>
          </div>

          {/* Big price headline */}
          <p
            className="font-heading font-extrabold text-ink leading-[1] mb-2 flex items-baseline justify-center lg:justify-start gap-1.5"
            data-testid="quote-result-price"
          >
            <span className="text-5xl sm:text-6xl">
              ${result.monthly_estimate_dollars.toFixed(0)}
            </span>
            <span className="text-base sm:text-lg text-slate-500 font-sans font-medium">
              / mo
            </span>
          </p>

          {/* Plan + bins sub-line */}
          <p className="text-[13px] sm:text-sm text-slate-600 mb-5">
            {result.suggested_plan === "monthly" ? (
              <>Monthly · {result.bin_count} bin{result.bin_count === 1 ? "" : "s"}</>
            ) : (
              <>Bi-Weekly · {result.bin_count} bins · 2 cleanings/mo</>
            )}
          </p>

          {/* CTA */}
          <button
            type="button"
            onClick={() =>
              navigate(
                `/signup?plan=${result.suggested_plan}&bins=${result.bin_count}&zip=${result.zip}`,
              )
            }
            className="h-12 sm:h-14 w-full inline-flex items-center justify-center gap-2 bg-brand text-white font-bold rounded-xl px-7 transition-all duration-200 hover:bg-brand-600 hover:-translate-y-0.5 hover:shadow-lift focus:outline-none focus:ring-4 focus:ring-brand/25"
            data-testid="quote-cta-signup"
          >
            Start My Service
            <ArrowRight size={16} />
          </button>
        </div>
      )}

      {result && result.in_service === false && (
        <CustomQuoteForm initialZip={zip} initialBins={bins} />
      )}

      <style>{`
        .quote-cta-shimmer { background-color: #0EA5E9; }
        .quote-cta-shimmer::before {
          content: "";
          position: absolute;
          inset: 0;
          background-image: linear-gradient(110deg,
            rgba(255,255,255,0) 0%,
            rgba(255,255,255,0) 38%,
            rgba(255,255,255,0.32) 50%,
            rgba(255,255,255,0) 62%,
            rgba(255,255,255,0) 100%);
          background-size: 220% 100%;
          background-repeat: no-repeat;
          background-position: -120% 0;
          animation: quote-shimmer 4s cubic-bezier(0.45, 0, 0.55, 1) 1.5s infinite;
          pointer-events: none;
        }
        .quote-cta-shimmer:hover { background-color: #0284C7; }
        @keyframes quote-shimmer {
          0%   { background-position: -120% 0; }
          55%  { background-position: 220% 0; }
          100% { background-position: 220% 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          .quote-cta-shimmer::before { animation: none !important; background-image: none !important; }
        }
      `}</style>
    </div>
  );
}

function CustomQuoteForm({ initialZip, initialBins }) {
  const INITIAL = {
    full_name: "",
    phone: "",
    email: "",
    service_address: "",
    zip: initialZip || "",
    bin_count: initialBins || 1,
    message: "",
  };
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (ev) => {
    const val =
      k === "bin_count" ? parseInt(ev.target.value, 10) || 1 : ev.target.value;
    setForm((f) => ({ ...f, [k]: val }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = "Please enter your name.";
    if (!form.phone.trim()) e.phone = "Phone number required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Enter a valid email.";
    if (!form.service_address.trim())
      e.service_address = "Service address required.";
    if (!/^\d{5}$/.test(form.zip)) e.zip = "5-digit zip required.";
    if (form.bin_count < 1) e.bin_count = "At least 1 bin.";
    if (!form.message.trim()) e.message = "Tell us what service you want.";
    return e;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    const v = validate();
    if (Object.keys(v).length) {
      setErrors(v);
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/custom-quote", {
        full_name: form.full_name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim().toLowerCase(),
        service_address: form.service_address.trim(),
        zip: form.zip,
        bin_count: form.bin_count,
        message: form.message.trim(),
        source: "out_of_area",
      });
      setSubmitted(true);
    } catch (err) {
      toast.error("Couldn't send your request", {
        description: formatApiError(err),
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        className="mt-5 rounded-2xl bg-accent-50 border border-accent-200 p-6 animate-fade-up"
        data-testid="custom-quote-success"
      >
        <div className="flex items-start gap-3">
          <CheckCircle2 size={22} className="text-accent-600 mt-0.5 shrink-0" />
          <div>
            <p className="font-heading text-xl font-bold text-ink mb-1.5">
              Thank you!
            </p>
            <p className="text-base text-slate-700 leading-relaxed">
              We received your custom quote request. We'll review your address,
              service needs, and bin count, then get back to you as soon as
              possible.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="mt-5 rounded-2xl bg-brand-50 border border-brand-100 p-5 sm:p-6 animate-fade-up"
      data-testid="custom-quote-form-block"
    >
      <h4 className="font-heading text-2xl sm:text-3xl font-extrabold text-ink mb-2 leading-tight">
        Need a custom quote?
      </h4>
      <p className="text-base text-slate-700 leading-relaxed mb-5">
        Every home and pickup setup can be a little different. Send us your
        name, phone number, email, address, how many bins you have, and what
        type of service you're looking for. We'll review everything and send
        you a custom quote.
      </p>

      <form
        onSubmit={onSubmit}
        noValidate
        className="grid grid-cols-1 sm:grid-cols-2 gap-4"
        data-testid="custom-quote-form"
      >
        <CqField
          label="Full Name"
          id="cq-name"
          value={form.full_name}
          onChange={update("full_name")}
          placeholder="Enter your full name"
          error={errors.full_name}
          testId="cq-input-name"
        />
        <CqField
          label="Phone Number"
          id="cq-phone"
          type="tel"
          value={form.phone}
          onChange={update("phone")}
          placeholder="Enter your phone number"
          error={errors.phone}
          testId="cq-input-phone"
        />
        <CqField
          label="Email Address"
          id="cq-email"
          type="email"
          value={form.email}
          onChange={update("email")}
          placeholder="Enter your email address"
          error={errors.email}
          testId="cq-input-email"
        />
        <CqField
          label="Service Address"
          id="cq-address"
          value={form.service_address}
          onChange={update("service_address")}
          placeholder="Enter your service address"
          error={errors.service_address}
          testId="cq-input-address"
        />
        <CqField
          label="Zip Code"
          id="cq-zip"
          value={form.zip}
          onChange={(ev) =>
            setForm((f) => ({
              ...f,
              zip: ev.target.value.replace(/\D/g, "").slice(0, 5),
            }))
          }
          inputMode="numeric"
          placeholder="08021"
          error={errors.zip}
          testId="cq-input-zip"
        />
        <div data-testid="cq-input-bins-wrap">
          <label
            htmlFor="cq-bins"
            className="block text-[11px] font-bold uppercase tracking-[0.18em] text-slate-700 mb-2"
          >
            Number of Bins
          </label>
          <select
            id="cq-bins"
            value={form.bin_count}
            onChange={update("bin_count")}
            className="h-12 w-full rounded-xl border-2 border-slate-200 bg-white px-4 text-base text-ink focus:outline-none focus:border-brand focus:ring-4 focus:ring-brand/15"
            data-testid="cq-input-bins"
          >
            {BIN_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n} {n === 1 ? "bin" : "bins"}
              </option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label
            htmlFor="cq-message"
            className="block text-[11px] font-bold uppercase tracking-[0.18em] text-slate-700 mb-2"
          >
            Service Request
          </label>
          <textarea
            id="cq-message"
            rows={4}
            value={form.message}
            onChange={update("message")}
            placeholder="Tell us what service you want"
            className={`w-full rounded-xl border-2 bg-white px-4 py-3 text-base text-ink placeholder:text-slate-400 focus:outline-none focus:border-brand focus:ring-4 focus:ring-brand/15 ${
              errors.message ? "border-red-400" : "border-slate-200"
            }`}
            data-testid="cq-input-message"
          />
          {errors.message && (
            <p className="mt-1.5 text-xs text-red-500">{errors.message}</p>
          )}
        </div>

        <div className="sm:col-span-2 mt-1">
          <button
            type="submit"
            disabled={submitting}
            className="w-full sm:w-auto h-14 inline-flex items-center justify-center gap-2 bg-brand text-white text-base lg:text-lg font-bold rounded-xl px-8 transition-all duration-200 hover:bg-brand-600 hover:-translate-y-0.5 hover:shadow-lift focus:outline-none focus:ring-4 focus:ring-brand/25 disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="cq-submit"
          >
            {submitting ? "Sending…" : "Request Custom Quote"}
            {!submitting && <ArrowRight size={16} />}
          </button>
        </div>
      </form>
    </div>
  );
}

function CqField({
  label,
  id,
  type = "text",
  value,
  onChange,
  placeholder,
  error,
  testId,
  inputMode,
}) {
  return (
    <div data-testid={`${testId}-wrap`}>
      <label
        htmlFor={id}
        className="block text-[11px] font-bold uppercase tracking-[0.18em] text-slate-700 mb-2"
      >
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        inputMode={inputMode}
        className={`h-12 w-full rounded-xl border-2 bg-white px-4 text-base text-ink placeholder:text-slate-400 focus:outline-none focus:border-brand focus:ring-4 focus:ring-brand/15 ${
          error ? "border-red-400" : "border-slate-200"
        }`}
        data-testid={testId}
      />
      {error && <p className="mt-1.5 text-xs text-red-500">{error}</p>}
    </div>
  );
}
